import { Box, Button } from "@mui/material";
import { SubmissionStepStatus, SubmissionStepType } from "generated-graphql/graphql";
import { useStepperNavigation } from "hooks/useStepperNavigation";
import { useTouchSubmissionMutation } from "hooks/useTouchSubmissionMutation";
import { useWasteStepper } from "hooks/useWasteStepper";
import { useCallback } from "react";

/**
 * Shared footer for stepper step editors. Marks the given step complete and
 * navigates to the next step in order.
 *
 * Renders nothing when the user cannot edit (read-only customers) or when the
 * submission is frozen (read-only after the snapshot is taken): there is no
 * "mark complete & continue" in either case.
 */
export function StepContinueFooter({
  submissionId,
  step,
}: {
  submissionId: string;
  step: SubmissionStepType;
}) {
  const { isFrozen, canEdit } = useWasteStepper();
  const { touchStep, loading } = useTouchSubmissionMutation(submissionId);
  const { goToNextStep } = useStepperNavigation();

  const handleContinue = useCallback(async () => {
    await touchStep(step, SubmissionStepStatus.Completed);
    goToNextStep(step);
  }, [touchStep, step, goToNextStep]);

  if (isFrozen || !canEdit) {
    return null;
  }

  return (
    <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
      <Button variant="contained" loading={loading} onClick={handleContinue}>
        Mark complete & continue
      </Button>
    </Box>
  );
}
